import Icon from "../components/common/icon/icon";
import React from "react";
import Button from "../components/common/button/button";
import { Typography, Box } from "@mui/material";
import { NavebarButtonsStyles } from "../components/common/navBar/navBar.styles";
import COMMON_CONSTANTS from "../constants/commonConstants";

const WORDPRESS_URL = COMMON_CONSTANTS.getWordpressBaseUrl();

// Function to create menu and sub-menu with required fields
const createDesktopNavMenu = (items) => {
    return items
        .filter(item => item?.acf?.device_visibility !== "mobile") // Filter items by device_visibility for Desktop
        .map((item) => {
            if (item?.child_items) { // If Menu has Submenus items
                return {
                    ID: item.ID,
                    title: item.title,
                    acf: item.acf,
                    menuItems: createDesktopNavSubMenu(item.child_items || []) // Create submenus for each item using a helper function
                }
            } else { // If the Main Menu is of type Button or Link
                if (item?.acf?.menu_type === 'Main Menu') {
                    const result = item?.acf?.isbutton ? {
                        ID: item?.ID,
                        title: item?.acf?.title,
                        link: item?.acf?.url || `/${item.slug}`,
                        name: item?.acf?.title,
                        children: (item?.acf.isbutton && item?.acf.custom_icon === 'Select Icon') ? createDesktopNavComponent.button(item?.acf, {
                            py: 2,
                            my: 2,
                            display: "block",
                            ...NavebarButtonsStyles,
                        }) : ((item?.acf.isbutton && item?.acf.custom_icon !== 'Select Icon') ? createDesktopNavComponent.iconButton(item?.acf) : createDesktopNavComponent.typography(item?.acf)),
                        menuItems: [],
                        acf: item?.acf
                    } : {};
                    return result;
                }
            }
        });
};

// Create sub-menu based on menu type
const createDesktopNavSubMenu = (subItems) => {

    const isShowHeader = (element) => element?.acf?.show_submenu_header === true;
    const addSubMenuHeader = subItems.some(isShowHeader); // Check if the any of the Submenus have header visible for desktop

    return subItems
        .filter(subItem => subItem?.acf?.device_visibility !== "mobile") // Filter items by device_visibility for Desktop
        .map((subItem) => {
            const { acf, title, child_items } = subItem;
            if (child_items?.length && child_items[0]?.acf.menu_type !== "Submenu Header") { // If Submenu type is not Submenu header
                const nestedMenuItems = createDesktopNavNestedItems(child_items); // Handle displaying of Submenu item
                return {
                    title,
                    items: nestedMenuItems,
                    isShowHeader: acf?.show_submenu_header,
                    icon: acf?.custom_icon && acf?.custom_icon !== "Select Icon" ? () => <Icon type={acf?.custom_icon} fontSize="small" color="#11728D" /> : null,
                    addSubMenuHeader: addSubMenuHeader
                };
            }

            return getDesktopNavSubCategory(subItem);
        });
};

//For Submenu having nested Submenu with it's own header and Submenu item
const getDesktopNavSubCategory = (subCategoryItem) => {
    const subcategories = createDesktopNavSubMenu(subCategoryItem.child_items || []);
    return { subcategories };
};

// Create Submenu navigation items
const createDesktopNavNestedItems = (childItems) => {
    return childItems
        .filter(item => item?.acf?.device_visibility !== "mobile") // Filter items by device_visibility for Desktop
        .map((item) => {
            const { acf, title, slug } = item;

            if (acf?.isbutton) return { children: createDesktopNavComponent.button(acf), name: acf?.title?.length > 0 ? acf.title : title, };
            if (acf?.istypography) return { children: createDesktopNavComponent.typography(acf), name: acf?.title?.length > 0 ? acf.title : title };

            const subMenuNestedItem = {
                label: title,
                link: acf.url || `/${slug}`,
                icon: acf?.custom_icon && acf?.custom_icon !== "Select Icon" ? () => <Icon color="#11728D" fontSize="small" type={acf?.custom_icon} /> : null,
            };

            if (acf.fontweight !== "") subMenuNestedItem.fontWeight = acf.fontweight;

            return subMenuNestedItem;
        });
};

// Create desktop menu item components based on ACF custom metadata
const createDesktopNavComponent = {
    button: (props, customStyle = {}) => <Button
        color={props.background_color}
        size={props.size}
        sx={{
            ...customStyle
        }}
        href={props?.url || '/'}
        variant={props?.button_variant}
    >
        {props.title}
    </Button>,
    typography: (props) => (
        <Typography fontFamily={props?.fontfamily} fontStyle={props?.fontstyle} fontWeight={props?.fontweight}>
            {props.add_html_content ?
                <div dangerouslySetInnerHTML={{ __html: props.html_content }} /> :
                props?.text_content}
        </Typography>
    ),
    icon: (props) => <Icon type={props?.custom_icon} fontSize="medium" color="#11728D" />,
    iconButton: (props) =>
        props?.custom_icon && (
            <Button
                color={
                    props.background_color === "text" ? "#000000" : props?.background_color
                }
                fontFamily="body.fontFamily.secondary"
                fontWeight="400"
                variant={props?.button_variant}
                href={props?.url}
                sx={{
                    fontSize: { xs: "13px", lm: "16px" },
                    my: 2,
                    display: "flex",
                    ...NavebarButtonsStyles,
                }}
            >
                <Box sx={{ px: "10px" }}>
                    <Icon
                        color={
                            props.background_color === "text" ? "#000000" : props?.background_color
                        }
                        sx={{
                            fontSize: { xs: "13px", lm: "16px" },
                        }}
                        type={props?.custom_icon}
                    />
                </Box>
                {props?.title}
            </Button>
        ),

};

//Filter and sort menu and submenu items based on Mobile Drawer Priority and Device Visibility
const createMobileDrawerMenu = (items) => {
    return items
        .filter(item => item?.acf?.device_visibility !== "desktop")  // Filter items by device_visibility for Mobile
        .reduce((acc, item) => {
            // Check if this item is a "Submenu Header" and if any child item has a mobileDrawerPriority
            if (item.acf?.menu_type === "Submenu Header" && item.submenus && Array.isArray(item.submenus) && item.submenus.length > 0) {
                // Check if any child item has a mobileDrawerPriority
                const hasPriorityChild = item.submenus.some(subItem => subItem.acf?.mobileDrawerPriority);

                // Merge child items to the parent level if sub menu header is hidden and any child item has mobileDrawerPriority
                if (hasPriorityChild || item.acf?.show_submenu_header === false) {
                    acc.push(...createMobileDrawerMenu(item.submenus));  // Recursively flatten submenus with priority
                } else {
                    // If no child items have mobileDrawerPriority, keep the item as it is
                    acc.push({ ...item, submenus: createMobileDrawerMenu(item.submenus) });  // Process submenus recursively
                }
            } else {
                // If it's not a submenu header or has no submenus, add the item to the list
                const adjustedItem = { ...item };
                if (Array.isArray(adjustedItem.submenus)) {
                    adjustedItem.submenus = createMobileDrawerMenu(adjustedItem.submenus);  // Recursively handle submenus
                }
                acc.push(adjustedItem);
            }

            return acc;
        }, [])
        // Sort the final array based on acf.mobileDrawerPriority if it's not empty
        .sort((a, b) => {
            const priorityA = a.acf?.mobileDrawerPriority;
            const priorityB = b.acf?.mobileDrawerPriority;

            // Convert to numbers for correct sorting
            const numPriorityA = priorityA ? parseInt(priorityA, 10) : Infinity;
            const numPriorityB = priorityB ? parseInt(priorityB, 10) : Infinity;

            // Compare the numeric priorities
            return numPriorityA - numPriorityB;
        });
};

//Create mobile drawer sub menu item from ACF custom metadata
const createMobileDrawerSubmenu = (items) => {
    return items
        .filter(item => item?.acf?.device_visibility !== "desktop")  // Filter items by device_visibility for Mobile
        .map(item => {
            if (item?.acf?.menu_type === 'Main Menu' && item?.acf?.isbutton) { //Handle buttons in main menu like Get a Quote or Retrieve a saved quote
                return {
                    ID: item?.ID,
                    title: item?.acf?.title,
                    link: item?.acf?.url || `/${item.slug}`,
                    name: item?.acf?.title,
                    acf: item?.acf,
                    submenus: []
                };
            } else {
                const filteredItem = {
                    name: item.title,
                    slug: item.slug,
                    acf: item.acf || {}
                };

                // Check for child_items and recursively filter them as submenus
                if (Array.isArray(item.child_items) && item.child_items.length > 0) {
                    filteredItem.submenus = createMobileDrawerSubmenu(item.child_items);  // Recursive call
                }

                return filteredItem;
            }

        });
}

//Create desktop footer vertical columns for top menus
const createFooterTopMenus = (items) => {
    return items
        .map(item => {
            let filteredItem = {
                acf: item.acf || {},
                url: item?.acf?.url || ''
            };

            if (item?.acf?.menuType === "Menu Header") {
                filteredItem['title'] = item?.acf?.isShowHeader ? item.title : null
            }
            if (item?.acf?.menuType === "Menu Item") {
                filteredItem['label'] = item.title
            }

            // Check for child_items and recursively filter them as submenus
            if (Array.isArray(item.child_items) && item.child_items.length > 0) {
                filteredItem.items = createFooterTopMenus(item.child_items);  // Recursive call for child_items
            }

            return filteredItem;
        });
};

// Create mobile footer sections for accordions
const createMobileFooterSections = (menuData) => {
    let filteredSections = [];
    let lastMenuHeader = null;

    // Create a deep copy of menuData to avoid mutating the original array
    let clonedMenuData = JSON.parse(JSON.stringify(menuData));

    clonedMenuData.forEach(item => {
        // Check if it's a "Menu Header" with isShowHeader === true
        if (item?.acf?.isShowHeader === true) {
            filteredSections.push(item);  // Add it as a new section
            lastMenuHeader = item; // Track the last "Menu Header"
        } else if (item?.acf?.isShowHeader === false && lastMenuHeader) {
            // Ensure items array is initialized for the last menu header
            if (!lastMenuHeader.items) {
                lastMenuHeader.items = [];
            }

            // Only add items if they don't already exist in lastMenuHeader.items
            item.items.forEach(newItem => {
                // Check if the item already exists by comparing the label
                const existingItem = lastMenuHeader.items.find(existingItem => existingItem.label === newItem.label);
                if (!existingItem) {
                    lastMenuHeader.items.push(newItem); // Add the item if it doesn't already exist
                }
            });
        }
    });

    // Filter out any Menu Header that doesn't have any items or is empty
    filteredSections = filteredSections.filter(section => section.items && section.items.length > 0);

    return filteredSections;
};

// Create footer social icons
const createFooterSocialIcons = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items.map(item => {
        return {
            url: item?.acf?.url || '',
            label: item?.title || '',
            type: item?.acf?.icon || 'Select Icon',
        };
    });
};

// Create footer links for bottom menus
const createFooterBottomMenus = (items) => {
    const links = items.filter(item => item?.acf?.orientation == "Horizontal" && item?.child_items?.some(child => child?.acf?.displayType !== "Social")).map(item => item.child_items)[0];
    const image = items.filter(item => item?.child_items?.some(child => child?.acf?.displayType === "Image")).map(item => item.child_items)[0];
    return {
        links: links ? links.map(item => {
            return {
                url: item?.acf?.url || '',
                label: item?.title || ''
            };
        }) : [],
        image: image && image[0]?.acf?.image?.length > 0 ? `${WORDPRESS_URL}${image[0].acf.image}` : null
    };
};

// Create footer legal text
const createFooterLegalText = (items) => {
    const paragraph = items.filter(item => item?.child_items?.some(child => child?.acf?.displayType === "Paragraph")).map(item => item.child_items)[0];
    return paragraph && paragraph[0]?.acf?.paragraphContent?.length > 0 ? paragraph[0].acf.paragraphContent : '';
};

const createQuotingFooterMenu = (items) => {
    const wordpressUrl = COMMON_CONSTANTS.getWordpressBaseUrl();
    return items.map(item => {
        const result = {
            isShowHeader: item.acf.isShowHeader,
            title: item.title,
            typography: null,
            image: null,
            faqs: null
        };

        // Loop through child items to find display types and add data in specific type
        for (let child of item.child_items) {
            if (child.acf.displayType === 'Typography' && result.typography === null) {
                result.typography = child.acf.typography;
            }
            if (child.acf.displayType === 'Image' && result.image === null) {
                result.image = `${wordpressUrl}${child.acf.image}`;
            }
            if (child.acf.displayType === 'FAQs' && result.faqs === null) {
                result.faqs = child.acf.faq;
            }
        }

        return result;
    });
}

/**
 * Format raw menu data from API into the structure needed by components.
 * This mirrors the formatting logic from navMenuService but is now separated
 * so components can format data on-demand from the store.
 */
export const formatNavMenuData = (menuId, data) => {
    const items = data?.items || [];
    
    if (menuId === 'footer_menu') {
        const verticalMenus = items.filter(item => item?.acf?.menuType == "Menu Header" && item?.acf?.orientation == "Vertical");
        const footerTopMenus = createFooterTopMenus(verticalMenus);
        const socialMenus = items.filter(item => item?.child_items?.some(child => child?.acf?.displayType === "Social")).map(item => item.child_items)[0];
        return {
            columns: footerTopMenus,
            mobileSections: createMobileFooterSections(footerTopMenus),
            socialIcons: createFooterSocialIcons(socialMenus),
            horizontalBar: createFooterBottomMenus(items),
            legalText: createFooterLegalText(items)
        };
    } else if (menuId === 'quoting_footer_menu') {
        return { menu: createQuotingFooterMenu(items), cpConfigs: data?.acf || {} };
    } else {
        // This block is for header navigation menus
        return {
            desktopNavMenu: createDesktopNavMenu(items),
            drawerCategories: createMobileDrawerMenu(createMobileDrawerSubmenu(items))
        };
    }
};

/**
 * Get empty menu state for a given menuId.
 * Used when menu data is not yet available or fetch fails.
 */
export const getEmptyMenuState = (menuId) => {
    if (menuId === 'footer_menu') {
        return {
            columns: [],
            mobileSections: [],
            socialIcons: [],
            horizontalBar: {},
            legalText: ''
        };
    } else if (menuId === 'quoting_footer_menu') {
        return { menu: [], cpConfigs: {} };
    } else {
        return {
            desktopNavMenu: [],
            drawerCategories: []
        };
    }
};
